import { useTracker } from '~/features/clickstream-sdk/composables/useTracker';
import { ETrackerActions } from '~/features/clickstream-sdk/models/actions';
import { ETrackerPages } from '~/features/clickstream-sdk/models/pages';
import { NavigationStore } from '~/features/store/navigation/business/useNavigationStore';
import type { TSkuCharacteristic } from '../models/sku-characteristic';

export function useProductPageTracker() {
  const { track } = useTracker({ page: ETrackerPages.PRODUCT, block: 'unknown', element: 'unknown' });
  const navigation = NavigationStore;

  // Функция для формирования списка доступных параметров товара
  const getProductParametersList = (characteristics: TSkuCharacteristic[]): string => {
    if (!characteristics || characteristics.length === 0) return JSON.stringify({});
    
    const parametersList: Record<string, string[]> = {};
    
    characteristics.forEach(char => {
      parametersList[char.title] = char.values.map(value => value.text);
    });
    
    return JSON.stringify(parametersList);
  };

  // Функция для формирования выбранных параметров товара
  const getSelectedProductParameters = (currentSku: any, characteristics: TSkuCharacteristic[]): string => {
    if (!currentSku || !characteristics || characteristics.length === 0) return JSON.stringify({});
    
    const selectedParameters: Record<string, string> = {};
    
    // Если у текущего SKU есть характеристики, используем их
    if (currentSku.characteristics && currentSku.characteristics.length > 0) {
      currentSku.characteristics.forEach((skuChar: any) => {
        const characteristic = characteristics.find(char => char.characteristicId === skuChar.characteristicId);
        if (characteristic) {
          const selectedValue = characteristic.values.find(value => value.valueId === skuChar.value.valueId);
          if (selectedValue) {
            selectedParameters[characteristic.title] = selectedValue.text;
          }
        }
      });
    }
    
    return JSON.stringify(selectedParameters);
  };

  const trackProductPageView = (productData: {
    productId: string;
    productName: string;
    categoryId?: string;
    categoryName?: string;
    price?: number;
    oldPrice?: number;
    brand?: string;
    skuId?: string;
    skuGroupId?: string;
    isFavorite?: boolean;
    rating?: number;
    reviewsCount?: number;
    availableAmount?: number;
    photos?: any[];
    badges?: string;
    badgesId?: number;
    sellerId?: number;
    externalRating?: boolean;
    video?: string;
    currentSku?: any;
    characteristics?: TSkuCharacteristic[];
  }) => {
    const isPictureList = productData.photos?.map(photo => photo ? true : false) || [];
    const source = navigation.previousRoute.value || '';
    
    return track({
      action: ETrackerActions.VIEW,
      product_id: productData.productId,
      product_parameters: getSelectedProductParameters(productData.currentSku, productData.characteristics || []),
      product_parameters_list: getProductParametersList(productData.characteristics || []),
      rating_value: Math.ceil((productData.rating || 0) * 10) / 10,
      source: source,
      is_picture_list: JSON.stringify(isPictureList),
      old_price: productData.oldPrice || 0,
      price: productData.price || 0,
      rev_num: productData.reviewsCount || 0,
      wishlist_toggle: productData.isFavorite || false,
      badges: productData.badges || '',
      shop_id: productData.sellerId || 0,
      is_parsing_rating: productData.externalRating || false,
      is_act: Boolean(productData.video),
      count_picture: productData.photos?.length || 0,
      sku_id: productData.skuId || '',
      badges_id: productData.badgesId || 0,
      clickable_badges: false,
    });
  };

  return {
    trackProductPageView,
  };
}
